import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Button, Space, Modal, Alert, Descriptions, Tooltip, Typography, Badge, message, notification, Divider } from 'antd';
import { RobotOutlined, CheckOutlined, CloseOutlined, InfoCircleOutlined, MailOutlined, ExclamationCircleOutlined, SyncOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text, Paragraph } = Typography;

// 模拟待确认的Agent操作数据
const mockAgentActions = [
  {
    id: 1,
    flight_number: 'MU5101',
    flight_date: '2024-07-15',
    route: 'SHA-PEK',
    transaction_type: 'FUEL',
    event_description: '燃油申请已被批准，数量为5000KG',
    status: 'WAITING_CONFIRM',
    created_at: dayjs().subtract(2, 'hour').toISOString(),
    operator: 'AI助手',
    related_email_id: 12345,
    agent_confidence: 0.95,
    agent_suggestion: '邮件确认燃油申请已被批准，总量5000公斤，建议将状态更新为已批准',
  },
  {
    id: 2,
    flight_number: 'CZ3102',
    flight_date: '2024-07-15',
    route: 'CAN-SHA',
    transaction_type: 'PERMIT',
    event_description: '许可申请被拒绝，需修改航路点',
    status: 'WAITING_CONFIRM',
    created_at: dayjs().subtract(3, 'hour').toISOString(),
    operator: 'AI助手',
    related_email_id: 12346,
    agent_confidence: 0.88,
    agent_suggestion: '邮件表明许可申请被拒绝，原因是需要修改航路点。建议将状态更新为已拒绝并通知航班计划部门。',
  },
  {
    id: 3,
    flight_number: 'CA1831',
    flight_date: '2024-07-16',
    route: 'PEK-SHA',
    transaction_type: 'SLOT',
    event_description: '时刻申请已获批准，请按新时刻安排航班',
    status: 'WAITING_CONFIRM',
    created_at: dayjs().subtract(5, 'hour').toISOString(),
    operator: 'AI助手',
    related_email_id: 12347,
    agent_confidence: 0.92,
    agent_suggestion: '邮件确认时刻申请已获批准，新时刻为07:30UTC。建议更新状态为已批准并通知航班计划和签派部门。',
  }
];

const typeColorMap = {
  "PERMIT": "blue",
  "FUEL": "green",
  "SLOT": "orange",
  "HANDLING": "purple",
  "ACTIVATION": "cyan",
  "MVT": "geekblue",
  "CREW": "magenta",
  "CATERING": "gold",
};

const AgentActionsPanel = () => {
  const [agentActions, setAgentActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentAction, setCurrentAction] = useState(null);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [emailPreviewVisible, setEmailPreviewVisible] = useState(false);
  const [currentEmailId, setCurrentEmailId] = useState(null);

  useEffect(() => {
    // 模拟加载数据
    const fetchAgentActions = async () => {
      setLoading(true);
      try {
        // 模拟API延迟
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // 获取待确认的Agent操作
        setAgentActions(mockAgentActions);
      } catch (error) {
        console.error('获取AI助手待确认事项失败:', error);
        messageApi.error('获取数据失败，请刷新页面重试');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAgentActions();
  }, []);
  
  const handleRefresh = async () => {
    setLoading(true);
    try {
      // 模拟API延迟
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // 刷新数据
      setAgentActions(mockAgentActions);
      messageApi.success('刷新成功');
    } catch (error) {
      console.error('刷新失败:', error);
      messageApi.error('刷新失败，请重试');
    } finally {
      setLoading(false);
    }
  };
  
  const handleConfirm = (record) => {
    setCurrentAction(record);
    setConfirmVisible(true);
  };
  
  const confirmAgentAction = async () => {
    if (!currentAction) return;
    
    setActionLoading(true);
    try {
      // 模拟API请求
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 更新本地数据
      const updatedActions = agentActions.filter(action => action.id !== currentAction.id);
      setAgentActions(updatedActions);
      
      messageApi.success('已确认AI助手的处理建议');
    } catch (error) {
      console.error('确认失败:', error);
      messageApi.error('确认失败，请重试');
    } finally {
      setActionLoading(false);
      setConfirmVisible(false);
      setCurrentAction(null);
    }
  };
  
  const rejectAgentAction = async () => {
    if (!currentAction) return;
    
    setActionLoading(true);
    try {
      // 模拟API请求
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 更新本地数据
      const updatedActions = agentActions.filter(action => action.id !== currentAction.id);
      setAgentActions(updatedActions);
      
      messageApi.info('已拒绝AI助手的处理建议');
    } catch (error) {
      console.error('拒绝失败:', error);
      messageApi.error('操作失败，请重试');
    } finally {
      setActionLoading(false);
      setConfirmVisible(false);
      setCurrentAction(null);
    }
  };
  
  const viewEmail = (emailId) => {
    setCurrentEmailId(emailId);
    setEmailPreviewVisible(true);
    messageApi.info(`查看邮件ID: ${emailId}（功能待实现）`);
  };
  
  const columns = [
    {
      title: '航班信息',
      dataIndex: 'flight_number',
      key: 'flight_info',
      render: (text, record) => (
        <Space direction="vertical" size="small">
          <Text strong>{text}</Text>
          <Text type="secondary">{record.route} / {dayjs(record.flight_date).format('YYYY-MM-DD')}</Text>
        </Space>
      ),
    },
    {
      title: '事务类型',
      dataIndex: 'transaction_type',
      key: 'transaction_type',
      render: (type) => (
        <Tag color={typeColorMap[type] || 'default'}>
          {type === "PERMIT" && "许可"}
          {type === "FUEL" && "燃油"}
          {type === "SLOT" && "时刻"}
          {type === "HANDLING" && "地服"}
          {type === "ACTIVATION" && "激活"}
          {type === "MVT" && "动态"}
          {type === "CREW" && "机组"}
          {type === "CATERING" && "餐食"}
        </Tag>
      ),
    },
    {
      title: '事件描述',
      dataIndex: 'event_description',
      key: 'event_description',
      ellipsis: true,
      render: (text, record) => (
        <Space>
          <RobotOutlined style={{ color: '#1890ff' }} />
          <span>{text}</span>
          <Tooltip title="AI分析置信度">
            <Tag color="blue">{(record.agent_confidence * 100).toFixed(0)}%</Tag>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: '检测时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            size="small" 
            icon={<CheckOutlined />} 
            onClick={() => handleConfirm(record)}
          >
            确认
          </Button>
          <Button
            type="primary"
            ghost
            size="small"
            icon={<MailOutlined />}
            onClick={() => viewEmail(record.related_email_id)}
          >
            查看邮件
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Card
        title={
          <Space>
            <RobotOutlined />
            <span>AI助手待确认事项</span>
            <Badge count={agentActions.length} style={{ backgroundColor: '#1890ff' }} />
          </Space>
        }
        extra={
          <Button
            icon={<SyncOutlined />}
            onClick={handleRefresh}
            loading={loading}
          >
            刷新
          </Button>
        }
      >
        {agentActions.length > 0 ? (
          <Table
            columns={columns}
            dataSource={agentActions}
            rowKey="id"
            pagination={false}
            loading={loading}
          />
        ) : (
          <Alert
            message="暂无待确认事项"
            description="目前没有需要确认的AI助手处理结果"
            type="info"
            showIcon
            icon={<RobotOutlined />}
          />
        )}
      </Card>
      
      {/* 确认对话框 */}
      <Modal
        title={
          <Space>
            <RobotOutlined />
            <span>确认AI助手处理建议</span>
          </Space>
        }
        open={confirmVisible}
        onCancel={() => {
          if (!actionLoading) {
            setConfirmVisible(false);
            setCurrentAction(null);
          }
        }}
        footer={[
          <Button 
            key="reject" 
            danger 
            onClick={rejectAgentAction}
            loading={actionLoading}
          >
            拒绝
          </Button>,
          <Button 
            key="view-email" 
            type="dashed"
            onClick={() => currentAction && viewEmail(currentAction.related_email_id)}
            disabled={!currentAction?.related_email_id}
          >
            查看原始邮件
          </Button>,
          <Button 
            key="confirm" 
            type="primary" 
            onClick={confirmAgentAction}
            loading={actionLoading}
          >
            确认
          </Button>,
        ]}
        width={600}
      >
        {currentAction && (
          <>
            <Alert
              message="AI助手已解析邮件内容并提出处理建议"
              description={
                <p>
                  根据航班 <Text strong>{currentAction.flight_number}</Text> ({currentAction.route}) 相关邮件，
                  AI助手检测到以下信息：
                </p>
              }
              type="info"
              showIcon
              icon={<RobotOutlined />}
              style={{ marginBottom: 16 }}
            />
            
            <Descriptions 
              bordered 
              size="small" 
              column={1}
              title={
                <Space>
                  <span>事务信息</span>
                  <Tooltip title="AI助手对结果的确信程度">
                    <Tag color="blue">
                      置信度: {(currentAction.agent_confidence * 100).toFixed(0)}%
                    </Tag>
                  </Tooltip>
                </Space>
              }
            >
              <Descriptions.Item label="航班号">{currentAction.flight_number}</Descriptions.Item>
              <Descriptions.Item label="航线">{currentAction.route}</Descriptions.Item>
              <Descriptions.Item label="航班日期">{dayjs(currentAction.flight_date).format('YYYY-MM-DD')}</Descriptions.Item>
              <Descriptions.Item label="事务类型">
                <Tag color={typeColorMap[currentAction.transaction_type] || "default"}>
                  {currentAction.transaction_type}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="事件描述">{currentAction.event_description}</Descriptions.Item>
              <Descriptions.Item label="AI建议">
                <Text strong>{currentAction.agent_suggestion}</Text>
              </Descriptions.Item>
            </Descriptions>
            
            <Divider />
            
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text type="secondary">
                请确认AI助手的处理建议是否正确。确认后将按照建议处理该事务，拒绝则保持事务为待处理状态。
              </Text>
              
              <Alert
                message="请注意"
                description="如果AI助手的处理建议不够明确或者有错误，您可以拒绝此建议并手动处理。"
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
              />
            </Space>
          </>
        )}
      </Modal>
    </>
  );
};

export default AgentActionsPanel; 